import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import SkeletonTable from "../../components/table/SkeletonTable";
import columns from "../../columns/payroll/viewPayroll";
import LeftArrow from "../../assets/LeftArrow";
import Delete from "../../assets/Delete";
import ModalComponent from "../../components/modals/ModalComponent";
import ConfirmationModal from "../../components/modals/ConfirmationModal";
import {
  AddUpdatePayrollData,
  DeletePayrollData,
  ExportPayrollApi,
  GetPayrollData,
  ViewPayrollHistoryApi,
} from "../../api/request/payroll/payroll";
import moment from "moment";
import addPayrollColumns from "../../columns/payroll/empViewHistory";
import LoadableButton from "../../components/button/LoadableButton";
import { DatePicker } from "antd";
import { ReactComponent as Email } from "../../assets/email.svg";
import { ReactComponent as Phone } from "../../assets/phone.svg";
import EditPayroll from "../../assets/EditPayroll";
import UploadFile from "../../assets/UploadFile";
import { saveAs } from "file-saver";
import GlobalTooltip from "../../components/common/GlobalTooltip";
const { RangePicker } = DatePicker;

const ViewPayRoll = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [docOpen, setDocOpen] = useState(false);
  const [docUrl, setDocUrl] = useState([]);
  const [isEmpDelete, setIsEmpDelete] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeHistory, setEmployeeHistory] = useState([]);
  const [isEmpHistoryLoading, setIsEmpHistoryLoading] = useState(false);
  const [isEmpDeleteLoading, setIsEmpDeleteLoading] = useState(false);
  const [isEmployeeModalOpen, setIsEmployeeModalOpen] = useState(false);
  const [isUpdateLoading, setIsUpdateLoading] = useState(false);
  const [range, setRange] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);

  const getViewPayrollData = async () => {
    try {
      setIsLoading(true);
      const { data } = await GetPayrollData();
      if (data?.status) {
        setData(
          data?.data?.map((item) => ({
            ...item,
            start_date: moment(item?.start_date).format("ll"),
            end_date: moment(item?.end_date).format("ll"),
            status: (
              <p
                className={`${
                  item?.status === "entered" ? "text-green-500" : "text-red-600"
                } font-bold capitalize`}
              >
                {item?.status}
              </p>
            ),
            doc: (
              <div
                className={`${
                  item?.documents?.length
                    ? "text-blue-500 underline cursor-pointer"
                    : ""
                }`}
                onClick={() => {
                  if (item?.documents?.length === 1) {
                    window.open(item?.documents[0]?.path, "_blank");
                  } else if (item?.documents?.length > 1) {
                    setDocUrl(item?.documents);
                    setDocOpen(true);
                  }
                }}
              >
                {item?.documents?.length ? "View" : "--"}
              </div>
            ),
            action: (
              <div className="flex gap-3">
                <GlobalTooltip title="Edit Payroll">
                  <button
                    onClick={() => {
                      setIsEmployeeModalOpen(true);
                      handleViewHistory(item);
                      setSelectedEmployee(item);
                    }}
                  >
                    <EditPayroll />
                  </button>
                </GlobalTooltip>

                <GlobalTooltip title="Delete Payroll">
                  <button
                    onClick={(e) => {
                      e?.stopPropagation();
                      setIsEmpDelete(true);
                      setSelectedEmployee(item);
                    }}
                  >
                    <Delete color="#000" className="!size-4" />
                  </button>
                </GlobalTooltip>
              </div>
            ),
          }))
        );
      }
    } catch (error) {
      if (error?.response?.data?.message)
        toast?.error(error?.response?.data?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setIsEmpDeleteLoading(true);
      const { data } = await DeletePayrollData({
        id: selectedEmployee?.id,
      });
      if (data?.status) {
        toast?.success(data?.message || "Employee deleted successfully");
        setIsEmpDelete(false);
        getViewPayrollData();
      }
    } catch (error) {
      toast?.error(error?.response?.data?.message || "Error deleting employee");
    } finally {
      setIsEmpDeleteLoading(false);
    }
  };

  const handleViewHistory = async (EmpData) => {
    if (!EmpData?.id) return;
    try {
      setIsEmpHistoryLoading(true);
      const { data } = await ViewPayrollHistoryApi({ id: EmpData?.id });
      if (data?.status) {
        setEmployeeHistory(
          data?.data[0]?.payroll_data?.map((item) => {
            return {
              ...item,
              email: (
                <div>
                  <p className="flex items-center">
                    <Email className="mr-1 size-3" />
                    {item?.email || "--"}
                  </p>
                  <p className="flex items-center">
                    <Phone className="mr-1 size-3" />
                    {item?.contact_no || "--"}
                  </p>
                </div>
              ),
            };
          }) || []
        );
      }
    } catch (error) {
      toast?.error(error?.response?.data?.message);
    } finally {
      setIsEmpHistoryLoading(false);
    }
  };

  const handleUpdate = async () => {
    try {
      setIsUpdateLoading(true);

      const payload = {
        id: selectedEmployee?.id,
        payroll_data: employeeHistory?.map((item) => ({
          payroll_emp_id: item?.payroll_emp_id || item?.id,
          name: item?.name,
          hours: item?.hours || 0,
          overtime_hour: item?.overtime_hour || 0,
          holidays: item?.holidays || 0,
          rate: item?.rate || 0,
        })),
      };
      if (range?.length === 2) {
        payload.start_date = range[0].format("YYYY-MM-DD");
        payload.end_date = range[1].format("YYYY-MM-DD");
      }

      const { data: res } = await AddUpdatePayrollData(payload);

      if (res?.status) {
        toast?.success(res?.message || "Payroll updated successfully");
        setIsEmployeeModalOpen(false);
        getViewPayrollData();
      } else {
        toast?.error(res?.message || "Payroll update failed");
      }
    } catch (error) {
      toast?.error(
        error?.response?.data?.message || "Error submitting payroll"
      );
    } finally {
      setIsUpdateLoading(false);
    }
  };

  const handleExportPayroll = async () => {
    try {
      setExportLoading(true);
      const headers = { responseType: "arraybuffer" };
      const res = await ExportPayrollApi({}, headers);

      const blob = new Blob([res?.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const fileName = "PayrollData.xlsx";
      saveAs(blob, fileName);
    } catch (error) {
      const msg = error?.response?.data?.error || "Failed to download file";
      toast?.error(msg);
    } finally {
      setExportLoading(false);
    }
  };

  const docColumns = [
    {
      title: "Document Name",
      dataIndex: "original_name",
      render: (value) => value || "--",
    },
    {
      title: "Action",
      dataIndex: "path",
      render: (path) => (
        <p
          onClick={() => window.open(path, "_blank")}
          className="text-blue-500 underline cursor-pointer"
        >
          View
        </p>
      ),
    },
  ];

  useEffect(() => {
    getViewPayrollData();
  }, []);

  return (
    <>
      <div className="border rounded-xl bg-white">
        <div className="flex items-center justify-between gap-3 mx-5 my-5">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/payroll")}
              className="text-secondary"
            >
              <LeftArrow />
            </button>
            <p className="title">
              View Payroll {data?.length ? `(${data?.length})` : ""}
            </p>
          </div>
          <LoadableButton
            onClick={() => handleExportPayroll()}
            isLoading={exportLoading}
            loadingLable="Exporting Payroll"
          >
            <UploadFile /> Export Payroll
          </LoadableButton>
        </div>
        <SkeletonTable columns={columns} data={data} isLoading={isLoading} />

        <ModalComponent
          isOpen={docOpen}
          setIsOpen={setDocOpen}
          title="View Document"
        >
          <SkeletonTable
            columns={docColumns}
            data={docUrl || []}
            pagination={false}
          />
        </ModalComponent>

        <ConfirmationModal
          isOpen={isEmpDelete}
          setIsOpen={setIsEmpDelete}
          message="Are you sure you want to delete this Payroll?"
          onConfirm={handleDelete}
          isLoading={isEmpDeleteLoading}
          loadingLabel="Deleting Payroll..."
          type="warning"
        />

        <ModalComponent
          isOpen={isEmployeeModalOpen}
          setIsOpen={setIsEmployeeModalOpen}
          title={
            <div>
              <p>View Payroll Employee History</p>
              <p className="text-base text-gray-500">
                [ {data[0]?.start_date} to {data[0]?.end_date} ]
              </p>
            </div>
          }
          width="60%"
        >
          <div className="flex w-full justify-end mb-2">
            <RangePicker onChange={(val) => setRange(val)} />
          </div>
          <SkeletonTable
            rowKey="payroll_emp_id"
            columns={addPayrollColumns(setEmployeeHistory)}
            data={employeeHistory || []}
            isLoading={isEmpHistoryLoading}
          />

          <div className="flex gap-2 mb-5">
            <LoadableButton
              className="btn btn-primary !px-4"
              onClick={handleUpdate}
              isLoading={isUpdateLoading}
              loadingLable="Adding Payroll..."
            >
              Save Payroll Details
            </LoadableButton>
          </div>
        </ModalComponent>
      </div>
    </>
  );
};

export default ViewPayRoll;
